import { AuthGuard } from '@/auth/auth-guards';
import { useAmplitudeFeatureFlags } from '@/composables/useAmplitudeFeatureFlags';
import { backOfficeMessages } from '@/views/gallery/back-office-messages';
import { RoutePaths } from '@/views/gallery/route-paths';
import { inventoryMessages } from '@/views/inventory/inventory-messages';
import { Permission } from '@art-basel/artbasel-lib-platform';
import type { RouteLocation, RouteRecordRaw } from 'vue-router';

const { featureFlags } = useAmplitudeFeatureFlags();

export const inventoryRoutes: Array<RouteRecordRaw> = [
  {
    meta: { title: inventoryMessages.inventoryTitleSeller },
    path: RoutePaths.INVENTORY,
    component: () => import('./inventory-page/InventoryPage.vue'),
    beforeEnter: (to: RouteLocation) => AuthGuard(to),
  },
  {
    meta: { title: inventoryMessages.inventoryCreateTitle },
    path: RoutePaths.INVENTORY_CREATE,
    component: () => import('./create/CreateItem.vue'),
    beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.CREATE_INVENTORY_ITEM),
  },
  {
    meta: { title: inventoryMessages.inventoryDetailTitle },
    path: RoutePaths.INVENTORY_DETAIL,
    component: () => import('./detail/ItemDetail.vue'),
    beforeEnter: (to: RouteLocation) => AuthGuard(to),
  },
  {
    meta: { title: inventoryMessages.inventoryEditTitle },
    path: RoutePaths.INVENTORY_EDIT,
    component: () => import('./edit/EditItem.vue'),
    beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.UPDATE_INVENTORY_ITEM),
  },
  {
    meta: { title: backOfficeMessages.bulkUploadTitle },
    path: RoutePaths.BULK_UPLOAD.ROOT,
    component: () => import('./bulk-upload/BulkUpload.vue'),
    beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.CREATE_INVENTORY_ITEM),
    children: [
      {
        meta: { title: backOfficeMessages.bulkUploadTitle },
        path: '',
        component: () => featureFlags.value.isBulkUploadV2Enabled
          ? import('./bulk-upload/components/BulkUploadFile.vue')
          : import('./bulk-upload/bulk-upload-v1/BulkUploadSelect.vue'),
        beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.CREATE_INVENTORY_ITEM),
      },
      {
        meta: { title: backOfficeMessages.bulkUploadTitle },
        path: RoutePaths.BULK_UPLOAD_SELECT,
        component: () => import('./bulk-upload/bulk-upload-v1/BulkUploadSelect.vue'),
        beforeEnter: [
          (to: RouteLocation) => AuthGuard(to, Permission.CREATE_INVENTORY_ITEM),
          () => {
            const { featureFlags } = useAmplitudeFeatureFlags();
            if (featureFlags.value.isBulkUploadV2Enabled) {
              return { path: RoutePaths.BULK_UPLOAD.ROOT };
            }
          },
        ],
      },
      {
        meta: { title: backOfficeMessages.bulkUploadTitle },
        path: RoutePaths.BULK_UPLOAD_PREVIEW,
        component: () => import('./bulk-upload/components/BulkUploadDataPreview.vue'),
        beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.CREATE_INVENTORY_ITEM),
      },
      {
        meta: { title: backOfficeMessages.bulkUploadTitle },
        path: RoutePaths.BULK_UPLOAD_CONNECT_ARTISTS,
        component: () => import('./bulk-upload/components/BulkUploadConnectArtists.vue'),
        beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.CREATE_INVENTORY_ITEM),
      },
      {
        meta: { title: backOfficeMessages.bulkUploadTitle },
        path: RoutePaths.BULK_UPLOAD_REVIEW,
        component: () => featureFlags.value.isBulkUploadV2Enabled
          ? import('./bulk-upload/components/BulkUploadReview.vue')
          : import('./bulk-upload/bulk-upload-v1/BulkUploadReview.vue'),
        beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.CREATE_INVENTORY_ITEM),
      },
      {
        meta: { title: backOfficeMessages.bulkUploadTitle },
        path: RoutePaths.BULK_UPLOAD_DONE,
        component: () => import('./bulk-upload/components/BulkUploadDone.vue'),
        beforeEnter: (to: RouteLocation) => AuthGuard(to, Permission.CREATE_INVENTORY_ITEM),
      },
    ],
  },
];
